<template>
  <vee-form v-slot="{ validate }" as="">
    <form
      ref="form"
      action="/subscription/process"
      method="post"
      class="standard-form"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <!-- step 1 - select option -->
      <div
        class="group -bg-02 -hs-medium -vs-medium"
        :class="{
          ...groupClasses,
          '-last': !formData.subscriptionPlan,
          '-middle': formData.subscriptionPlan,
        }"
      >
        <div class="body">
          <!-- recurring options -->
          <form-fieldset-slot
            legend="Select an auto-renew billing option"
            input-name="subscriptionPlan"
          >
            <radio-input
              v-for="subscriptionPlan in autoRenewPlans"
              :key="subscriptionPlan.vendorPlanId"
              v-model="formData.subscriptionPlan"
              :input-id="`recurring_${subscriptionPlan.vendorPlanId}`"
              name="subscriptionPlan"
              :checked-value="subscriptionPlan.id"
              rules="required"
              validate-as="Billing option"
              :show-error-msg="false"
            >
              <template #label>
                <!-- discount vs standard price -->
                <div v-if="isPlanDiscounted(subscriptionPlan)">
                  {{ subscriptionPlan.name }}
                  <span class="strike-price">
                    ${{ subscriptionPlan.price }}/{{
                      billingCycleName(subscriptionPlan.billingCycleMonths, false)
                    }}
                  </span>
                  <div class="sales-price">
                    <div
                      v-if="subscriptionPlan.discount.code === 'renewal-discount'"
                      class="on-sale"
                    >
                      Renewal Discount!
                    </div>
                    <div
                      v-else-if="subscriptionPlan.discount.code === 'referral-discount'"
                      class="on-sale"
                    >
                      Referral Discount!
                    </div>
                    <div v-else class="on-sale">Currently on Sale!</div>
                    {{ planDiscountFormatted(subscriptionPlan) }}
                  </div>
                </div>
                <div v-else>
                  {{ subscriptionPlan.name }}
                  <span class="sales-price">
                    ${{ subscriptionPlan.price }}/{{
                      billingCycleName(subscriptionPlan.billingCycleMonths, false)
                    }}
                  </span>
                </div>
              </template>
            </radio-input>
          </form-fieldset-slot>
        </div>
      </div>

      <!-- step 2 and 3 -->
      <div
        v-if="formData.subscriptionPlan"
        class="group -last -bg-02 -hs-medium -vs-medium"
        :class="groupClasses"
      >
        <div class="" aria-live="polite">
          <h2 ref="sectionHeader" class="heading -small" tabindex="-1">
            {{ sectionTitle }}
          </h2>
        </div>
        <div class="body">
          <!-- only show payment options once a plan is selected -->
          <div class="payment">
            <!-- step 2 - billing -->
            <div v-show="step === 'billing'" class="">
              <billing-address-form
                :initial-billing-address="initialBillingAddress"
                :auto-submit="!!initialBillingAddress"
                action-type="subscription"
                @billing-submit-action="handleBillingSubmit"
              />
            </div>

            <!-- step 3 payment -->
            <div v-show="step === 'payment'">
              <!-- billing review -->
              <billing-address-review
                v-if="billingAddress"
                :three-d-secure-billing-address="billingAddress.threeDSecure"
                @billing-back-action="goToBillingStep"
              />

              <!-- wallet options -->
              <div v-if="hasWalletBalance" class="cart-payment-wallet">
                <checkbox-input v-model="formData.useWallet" input-id="useWallet">
                  <template #label>
                    <span>
                      Apply FB Wallet Funds
                      <span class="wallet-balance">
                        - Current Balance: {{ moneyFormat(walletBalance.toString()) }}
                      </span>
                    </span>
                  </template>
                </checkbox-input>

                <text-input
                  v-show="formData.useWallet"
                  v-model="formData.walletAmount"
                  input-id="walletAmount"
                  type="number"
                  label="Amount to use from your wallet:"
                  placeholder="0.00"
                  :rules="walletAmountRules"
                  validate-as="Wallet amount"
                  :max="maxWalletAmount"
                  min="0"
                  @input="restrictWalletAmount"
                />
              </div>

              <!-- braintree payment required if recurring OR balance due -->
              <div>
                <!-- recurring + wallet messaging -->
                <div v-show="formData.useWallet">
                  <h2 class="heading -small">Recurring Payment Method</h2>
                  <p v-if="walletBillingCyclesCovered === 0">
                    <strong>{{ moneyFormat(formData.walletAmount) }}</strong> from your wallet will
                    be applied to your subscription. Your first payment will be will be
                    <strong>
                      {{ moneyFormat(orderBalance) }}
                      {{ freeTrial ? '(after your 7 day free trial)' : '' }}
                    </strong>

                    before returning to

                    <strong
                      v-if="planHasDiscount && selectedPlan.discount.numberOfBillingCycles > 1"
                      >{{ moneyFormat(planTotal) }}</strong
                    >
                    <strong v-else>{{ moneyFormat(planRegPrice) }}</strong
                    >. If you choose to cancel your subscription, any unused FB Wallet funds will be
                    returned to your wallet.
                  </p>
                  <p v-if="walletBillingCyclesCovered > 0">
                    <strong
                      >{{ moneyFormat(formData.walletAmount) }} from your wallet will fully cover
                      your {{ selectedPlan.name }} subscription for
                      {{ walletBillingCyclesCovered }} billing cycle(s).</strong
                    >
                    Please choose a payment method for recurring payments after your wallet amount
                    is depleted. If you choose to cancel your subscription, any unused FB Wallet
                    funds will be returned to your wallet.
                  </p>
                </div>

                <div class="cart-payment-widget">
                  <callout
                    v-if="paymentInfoError"
                    ref="paymentError"
                    type="error"
                    :closable="false"
                  >
                    <div>{{ paymentInfoError }}</div>
                  </callout>

                  <braintree-web-drop-in
                    :token="token"
                    :total="planTotal"
                    payment-flow-type="recurring"
                    @payment-drop-in-loaded="onDropInLoad"
                    @three-d-secure-customer-canceled="customerCanceled"
                  />
                </div>
              </div>

              <checkbox-input
                v-model="formData.tos"
                input-id="tos"
                checked-value="agree"
                unchecked-value=""
                rules="required"
                validate-as="Terms of service"
              >
                <template #label>
                  <span>
                    I agree to the FitnessBlender
                    <a href="/page/terms-of-use-agreement" target="_blank" rel="noopener noreferrer"
                      >terms of service</a
                    >
                    and
                    <a href="/page/privacy-policy" target="_blank" rel="noopener noreferrer"
                      >privacy policy</a
                    >
                  </span>
                </template>
              </checkbox-input>

              <checkbox-input
                v-model="formData.noRefunds"
                input-id="noRefunds"
                checked-value="agree"
                unchecked-value=""
                rules="required"
                validate-as="Agree to no refunds"
              >
                <template #label>
                  <span>
                    I understand that this is an add-on service and accept the service "as is" with
                    <strong>no refunds</strong>.
                  </span>
                </template>
              </checkbox-input>

              <csrf-input />
              <input
                id="paymentMethodNonce"
                name="paymentMethodNonce"
                type="hidden"
                :value="formData.nonce"
              />
              <input
                id="paymentType"
                name="paymentType"
                type="hidden"
                :value="formData.paymentType"
              />

              <!-- billing address - encoded json -->
              <input
                id="billingAddressEncoded"
                name="billingAddressEncoded"
                type="hidden"
                :value="formData.billingAddressEncoded"
              />

              <input id="code" name="code" type="hidden" :value="couponCode" />

              <submit-button
                type="submit"
                class="btn -main -block"
                :submitting="processing || (hasOrderBalance && !braintreeInstance)"
                label="Join FB Plus"
                :submitting-label="submittingLabel"
                @submit-action="handleSubmit(validate)"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { compareFloats, divideFloatsAsInts, moneyFormat } from '@/utils/helpers'
import { initExtraValidationRules } from '@/utils/forms/validation-rules'
import { Form as VeeForm } from 'vee-validate'
import TextInput from '@/components/forms/text-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import RadioInput from '@/components/forms/radio-input.vue'
import FormFieldsetSlot from '../forms/form-fieldset-slot.vue'
import SubmitButton from '../buttons/submit-button.vue'
import CsrfInput from '../forms/csrf-input.vue'
import BillingAddressForm from '../cart/billing-address-form.vue'
import BillingAddressReview from '../cart/billing-address-review.vue'
import Callout from '../notifications/callout.vue'
import BraintreeWebDropIn from '@/components/forms/braintree-web-drop-in.vue'
import { scrollToElemTop } from '@/utils/scroll-to'

/**
 * Multiple step payment form for subscriptions. This flow is a bit different
 * than cart checkout, so separate component.
 *
 * - step 1: select subscription plan
 * - step 2: billing address (see billing-address-form.vue)
 * - step 3: payment information (handled here, traditional form submit)
 *
 * On step 2, billing can be "auto submitted"
 * - pass in initial-billing-address (EX: BillingAddress::find())
 * - billing-submit-action event will be emitted as soon as related data and formatting is complete
 *
 * On step 3, user can use any of the following:
 * - 100% FB wallet paid (only for single month / non recurring plans)
 * - FB wallet + braintree
 * - 100% braintree paid
 */
export default defineComponent({
  name: 'SubscriptionCheckoutForm',
  components: {
    VeeForm,
    TextInput,
    CheckboxInput,
    RadioInput,
    FormFieldsetSlot,
    SubmitButton,
    CsrfInput,
    BillingAddressForm,
    BillingAddressReview,
    Callout,
    BraintreeWebDropIn,
  },
  props: {
    // should match BillingAddress model (underscore properties)
    initialBillingAddress: {
      type: Object,
      default: () => null,
    },
    autoRenewPlans: {
      type: Array,
      default: () => [],
    },
    couponCode: {
      type: String,
      default: '',
    },
    token: {
      type: String,
      required: true,
    },
    freeTrial: {
      type: Boolean,
      default: false,
    },
    // form input, so string
    walletBalance: {
      type: String,
      default: '0',
    },
    groupClasses: {
      type: String,
      default: '-narrow',
    },
  },
  data() {
    return {
      // track billing vs payment step
      step: 'billing',

      // step 2 manages this data
      // if step 2 is auto-submitted, then it will be populated as if step 2 was
      // filled out based in initialBillingAddress prop
      billingAddress: null,

      // payment form
      formData: {
        subscriptionPlan: '',
        tos: [],
        noRefunds: [],
        useWallet: this.walletBalance > 0,
        walletAmount: '0.00',
        nonce: '',
        paymentType: '',
        billingAddressEncoded: '',
      },
      processing: false,
      braintreeInstance: null,
      paymentInfoError: '',
    }
  },

  /* computed */
  computed: {
    ...mapState(useUserAuthStore, ['user']),

    // powers main section header
    // when step changes, he h1 with this property will be focused
    sectionTitle() {
      // step 2 OR auto-submitting step 1 (to avoid title and focus change)
      if (this.step === 'payment' || (this.initialBillingAddress && !this.billingAddress)) {
        return 'Payment Information'
      }

      // step 1
      return 'Billing Information'
    },

    // validation rules if useWallet is checked
    walletAmountRules() {
      if (this.formData.useWallet) {
        return `required|max_value:${this.maxWalletAmount}|min_value:0.01`
      }

      return ''
    },

    // check to see if string balance is truthy
    hasWalletBalance() {
      return !!Number.parseFloat(this.walletBalance)
    },

    hasOrderBalance() {
      return !!Number.parseFloat(this.orderBalance)
    },

    selectedPlan() {
      if (!this.formData.subscriptionPlan) {
        return null
      }

      return this.autoRenewPlans.find((sp) => sp.id === this.formData.subscriptionPlan)
    },

    /**
     * Users can enter wallet amounts more than plan total for recurring plans
     */
    maxWalletAmount() {
      return this.walletBalance
    },

    /**
     * set the default wallet amount to be the plan price, but
     * users can put more wallet bucks if they want, see maxWalletAmount
     */
    defaultWalletAmount() {
      if (compareFloats(this.walletBalance, '<=', this.planTotal)) {
        return this.walletBalance
      }

      return this.planTotal
    },

    /**
     * The number of billing cycles covered by the wallet discount when created
     */
    walletBillingCyclesCovered() {
      // planTotal is already the discounted amount if there is a discount
      const planTotal = this.planTotal
      const salePrice = planTotal
      const planRegPrice = this.selectedPlan.price
      const walletAmount = this.formData.walletAmount

      // check if plan is on sale, calculate cycles covered based on sale price
      if (
        this.selectedPlan.discount &&
        this.selectedPlan.discount.amount !== '0.00' &&
        this.selectedPlan.discount.numberOfBillingCycles > 0
      ) {
        // TODO: finish searching other discount areas
        let saleIntervalsCovered = Math.floor(divideFloatsAsInts(walletAmount, salePrice))

        // if all sale intervals are covered by wallet, add additional based on amount
        // of regular billing cycles covered
        if (saleIntervalsCovered > this.selectedPlan.discount.numberOfBillingCycles) {
          // reset saleIntervalsCovered to sales duration
          saleIntervalsCovered = this.selectedPlan.discount.numberOfBillingCycles
          const leftOverWalletAmount = walletAmount - saleIntervalsCovered * salePrice

          // total of sale and regular price billing cycles covered
          return (
            saleIntervalsCovered +
            Math.floor(divideFloatsAsInts(leftOverWalletAmount, planRegPrice))
          )
        }

        // total of sale billing cycles covered
        return saleIntervalsCovered
      }

      // total of regular price billing cycles covered
      return Math.floor(divideFloatsAsInts(walletAmount, planTotal))
    },

    submittingLabel() {
      if (!this.braintreeInstance) {
        return 'Loading...'
      }

      return 'Processing...'
    },

    // get the plan total, minus any discount
    planTotal() {
      if (this.formData.subscriptionPlan) {
        const { subscriptionPlan } = this.formData
        const subPlan = this.autoRenewPlans.find((sp) => sp.id === subscriptionPlan)
        const discount = subPlan.discount ? subPlan.discount.amount : '0.00'
        return (Number.parseFloat(subPlan.price) - Number.parseFloat(discount)).toFixed(2)
      }

      return '0.00'
    },

    // get the plan total without discount, used when paying with wallet
    // and if plan on sale... to show the original plan price to the user
    // after the wallet discount expires
    planRegPrice() {
      if (this.formData.subscriptionPlan) {
        const { subscriptionPlan } = this.formData
        const subPlan = this.autoRenewPlans.find((sp) => sp.id === subscriptionPlan)
        return subPlan.price
      }

      return '0.00'
    },

    planHasDiscount() {
      if (this.formData.subscriptionPlan) {
        const { subscriptionPlan } = this.formData
        const subPlan = this.autoRenewPlans.find((sp) => sp.id === subscriptionPlan)
        return subPlan.discount && subPlan.discount.amount !== '0.00'
      }

      return false
    },

    // default to order total, but if wallet is used, remaining balance
    orderBalance() {
      // no balance, full order total
      // if user not using wallet, full total
      // if wallet amount is falsy, full order total
      if (!this.hasWalletBalance || !this.formData.useWallet) {
        return this.planTotal
      }

      // assume user is attempting to use wallet balance
      const walletAmount = Number.parseFloat(this.formData.walletAmount || '0.00')
      const total = Number.parseFloat(this.planTotal)

      // should never be more than order total
      let balance = total - walletAmount
      return balance.toFixed(2)
    },
  },

  /* watchers */
  watch: {
    // set walletAmount on plan change
    'formData.subscriptionPlan': function (newPlan, prevPlan) {
      // on plan change, set wallet amount value
      if (newPlan !== prevPlan && this.hasWalletBalance) {
        this.formData.walletAmount = this.defaultWalletAmount
      }
    },

    // focus the section header on step change for accessibility
    sectionTitle(newTitle, prevTitle) {
      if (newTitle !== prevTitle) {
        this.$refs.sectionHeader.focus()
      }
    },

    // scroll on payment error
    paymentInfoError(newError, prevError) {
      if (!newError) {
        return
      }

      if (newError === prevError) {
        return
      }

      // must do nextTicket as callout component that contains error is in v-if
      this.$nextTick(() => {
        if (this.$refs && this.$refs.paymentError && this.$refs.paymentError.$el) {
          scrollToElemTop({
            elem: this.$refs.paymentError.$el,
            ms: 250,
          })
        }
      })
    },
  },

  /* lifecycle */
  created() {
    initExtraValidationRules()

    // update selected plan if only one option.... doing this in lifecycle
    // so watcher is triggered for formData.subscriptionPlan
    // auto select plan if only one plan
    const selectedPlan = this.autoRenewPlans.length === 1 ? this.autoRenewPlans[0].id : ''

    if (selectedPlan) {
      this.formData.subscriptionPlan = selectedPlan
    }
  },
  methods: {
    goToBillingStep() {
      this.step = 'billing'
    },

    // update billing address information from step 1 submit
    // OR step1 auto submit with initialBillingAddress pre-filled
    handleBillingSubmit(billingAddress) {
      this.billingAddress = billingAddress
      this.step = 'payment'
    },

    // use for display
    moneyFormat(val) {
      return moneyFormat(val)
    },

    billingCycleName(billingCycleMonths, capitalize = true) {
      const interval = billingCycleMonths === 12 ? 'Yearly' : 'Monthly'
      return capitalize ? interval : interval.toLowerCase()
    },

    isPlanDiscounted(subPlan) {
      return !!(subPlan.discount && subPlan.discount.amount !== '0.00')
    },

    planDiscountFormatted(subPlan) {
      if (!this.isPlanDiscounted(subPlan)) {
        return ''
      }

      const discountPrice = (
        Number.parseFloat(subPlan.price) - Number.parseFloat(subPlan.discount.amount)
      ).toFixed(2)

      const billingCycleName = this.billingCycleName(subPlan.billingCycleMonths, false)

      const durationBillingCycleName = subPlan.billingCycleMonths === 12 ? 'year' : 'month'

      // only one billing cycle discounted
      if (subPlan.discount.numberOfBillingCycles === 1) {
        return `$${discountPrice} for the first ${durationBillingCycleName}`
      }

      // multiple discounted billing cycles
      return `$${discountPrice}/${billingCycleName} for the first ${subPlan.discount.numberOfBillingCycles} ${durationBillingCycleName}s`
    },

    // handle child event emitter
    onDropInLoad(dropIn) {
      this.braintreeInstance = dropIn
    },

    // the customer canceled the 3D Secure challenge modal
    customerCanceled() {
      this.paymentInfoError =
        'Card authentication canceled. Please try again or select another payment method.'
    },

    /**
     * do not allow formData.walletAmount to ever be larger than total
     */
    restrictWalletAmount(e) {
      // force string, vue3 converts input type number to string
      const newAmount = `${e.target.value}`
      const maxWalletAmount = this.maxWalletAmount

      // over max amount, so just set to max
      if (compareFloats(newAmount, '>', maxWalletAmount)) {
        this.formData.walletAmount = maxWalletAmount
        return
      }

      // negative
      if (compareFloats(newAmount, '<', '0.00')) {
        this.formData.walletAmount = '0.00'
        return
      }

      // do not allow more than two decimal places
      if (newAmount.indexOf('.') !== -1 && newAmount.split('.')[1].length > 2) {
        this.formData.walletAmount = Number.parseFloat(newAmount).toFixed(2)
      }
    },

    /**
     * Traditional form submit
     * - single month + no balance = no braintree required
     * - single month + balance = braintree required
     * - recurring plans always require braintree
     */
    async handleSubmit(validate) {
      this.processing = true

      // require billing for any payment method
      if (!this.billingAddress) {
        this.processing = false
        this.goToBillingStep()
        return
      }

      // json encoded billing address
      this.formData.billingAddressEncoded = this.billingAddress.jsonEncoded

      // first pass validation
      const results = await validate()

      // invalid form
      if (!results.valid) {
        this.processing = false
        return
      }

      // if we get here, there is an order balance (regardless of fb wallet usage)
      // and braintree is required

      // if no drop in ui
      if (!this.braintreeInstance) {
        this.processing = false
        return
      }

      try {
        // reset braintree specific error
        this.paymentInfoError = ''

        // total needed for 3ds
        // if recurring, always use the plan total so 3ds knows what default / future payments will be
        const total = this.planTotal
        const selectedPlan = this.selectedPlan

        // 3d secure parameters
        // @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#requestPaymentMethod
        // @see https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
        const threeDSecureParameters = {
          amount: total,
          email: this.user.email || '',

          // per braintree, for recurring transactions we should request a challenge
          // if it is available
          // @see https://developers.braintreepayments.com/guides/3d-secure/migration/javascript/v3#recurring-transactions
          challengeRequested: true,

          // parse to plain object from observer
          billingAddress: JSON.parse(JSON.stringify(this.billingAddress.threeDSecure)),

          // digital goods delivered electronically
          additionalInformation: {},
        }

        // default additional information (only separated out for flow typing)
        threeDSecureParameters.additionalInformation.productCode = 'DIG'
        threeDSecureParameters.additionalInformation.deliveryTimeframe = '01'
        threeDSecureParameters.additionalInformation.deliveryEmail = this.user.email || ''

        // set recurring settings
        // braintree uses multiples of 28 days per cycle
        threeDSecureParameters.additionalInformation.recurringFrequency = `${
          28 * selectedPlan.billingCycleMonths
        }`
        // end 3ds params

        this.braintreeInstance.requestPaymentMethod(
          {
            threeDSecure: threeDSecureParameters,
          },
          (err, payload) => {
            if (err) {
              // No payment method is available.
              // An appropriate error will be shown in the UI.
              console.error(err)

              if (err.code && err.code === 'THREEDS_CARDINAL_SDK_ERROR') {
                // 3d secure specific
                this.paymentInfoError =
                  '3d Secure card authentication failed. Please click "Choose another way to pay" to try again.'
                this.braintreeInstance.clearSelectedPaymentMethod()

                // re-hide any vaulted cards in the UI
                if (this.braintreeInstance.fbCustomizeRecurringUI) {
                  this.braintreeInstance.fbCustomizeRecurringUI()
                }
              } else {
                // default
                this.paymentInfoError = 'Payment information is required.'
              }

              // re-enable form for submits
              this.processing = false
              return
            }

            // 3d secure user cancel handling as it is not handled
            // in the callback above, but on a braintreeInstance event
            if (this.paymentInfoError) {
              this.processing = false
              return
            }

            // Add the nonce to the form and submit
            this.formData.nonce = payload.nonce

            // CreditCard, ApplePayCard, PayPalAccount
            // @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#requestPaymentMethod
            this.formData.paymentType = payload.type

            this.$nextTick(() => {
              this.$refs.form.submit()
            })
          },
        )
      } catch (e) {
        // re-enable form for submits
        console.error('err with braintreeInstance.requestPaymentMethod', e)
        this.processing = false
      }
    },
  },
})
</script>
